import { useState, useEffect } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { OrderStatusBadge, PaymentStatusBadge } from "@/components/customer/OrderStatusBadge";
import { orderErrorMessage } from "@/components/customer/OrdersMessages";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CUSTOMER_ORDERS_ENABLED } from "@/lib/customer/feature-visibility";
import { CUSTOMER_ORDERS_PAGE_SIZE, useCustomerOrderList } from "@/hooks/useCustomerOrders";
import { formatISTDateTime } from "@/lib/date/ist";
import { formatPaise } from "@/lib/format/money";
import { RefreshCcw } from "lucide-react";
import { mapOrderStatus } from "@/lib/customer/status-mappers";
import { OrderProgress } from "@/components/customer/OrderProgress";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_customer/account/orders/")({
  beforeLoad: () => {
    if (!CUSTOMER_ORDERS_ENABLED) throw redirect({ to: "/account", replace: true });
  },
  component: OrdersListPage,
  head: () => ({
    meta: [
      { title: "Your orders | Poonam's Healthy Kitchen" },
      {
        name: "description",
        content: "View your past Poonam's Healthy Kitchen orders, their status and totals.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function OrdersListPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching, dataUpdatedAt, refetch } = useCustomerOrderList(page);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt).toLocaleTimeString());
    }
  }, [dataUpdatedAt]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / CUSTOMER_ORDERS_PAGE_SIZE));
  const rows = data?.rows ?? [];

  return (
    <AccountShell title="Your orders" description="Every order placed while signed in to this account.">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={isRefreshing || isLoading}
            className="h-8 text-xs gap-2"
          >
            <RefreshCcw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
            {isRefreshing ? "Refreshing..." : "Refresh Status"}
          </Button>
          {lastUpdated && !isLoading && (
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              Last updated: {lastUpdated}
            </div>
          )}
        </div>
        {isRefreshing && <span className="text-[10px] text-muted-foreground animate-pulse">Refreshing...</span>}
      </div>
      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading your orders</span>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p role="alert" className="rounded-lg border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          {orderErrorMessage(error)}
        </p>
      ) : rows.length === 0 ? (
        <section
          aria-labelledby="orders-empty-heading"
          className="rounded-lg border border-border/60 bg-card p-8 text-center"
        >
          <h2 id="orders-empty-heading" className="text-base font-semibold">
            No orders yet
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
            Orders you place while signed in will appear here with their status and totals.
          </p>
          <Button asChild className="mt-5">
            <Link to="/products">Browse the menu</Link>
          </Button>
        </section>
      ) : (
        <>
          <ul className="space-y-4">
            {rows.map((o) => (
              <li key={o.order_id}>
                <Link
                  to="/account/orders/$orderId"
                  params={{ orderId: o.order_id }}
                  className="block rounded-xl border border-border/60 bg-card p-6 transition-all hover:border-primary/30 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                    <div>
                      <p className="font-semibold text-lg">{o.order_ref ?? "Order"}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatISTDateTime(o.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{formatPaise(o.total_amount_paise)}</p>
                      <div className="mt-2 flex flex-col items-end gap-1.5">
                        <div className="flex flex-col items-end">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-1">Order Status</span>
                          <OrderStatusBadge status={o.status} />
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none mb-1">Payment</span>
                          <PaymentStatusBadge status={o.payment_status} />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border/40 pt-4">
                    <OrderProgress status={o.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
            <p className="text-xs text-muted-foreground" aria-live="polite">
              Page {page} of {totalPages} · {totalCount} {totalCount === 1 ? "order" : "orders"} in
              total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </AccountShell>
  );
}